import {
  Badge,
  DropdownMenu,
  tokens,
  Icon,
  DropdownSelectTrigger,
  useNotify,
} from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { forwardRef } from 'react';

import { supportedLocales, extendedSupportedLocales } from '../../utils/supportedLocales';
import { localeCountryCodeMap } from './utils';
import { userLocaleApi } from '../../api/userLocaleApi';

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacer.x1};
  border: 0;
  outline: none;
  ${tokens.transition('all')};
  cursor: pointer;
  border-radius: ${tokens.radius.m};
  background-color: transparent;
  ${tokens.elevation(0)};

  &:focus {
    ${tokens.focus()};
  }
  &:active {
    ${tokens.elevation(-1)};
    background-color: ${tokens.color.bg_active};
  }
  &:hover {
    background-color: ${tokens.color.bg_hover};
  }
  height: ${tokens.spacer.x5};
`;

const StyledItemText = styled.span`
  flex-grow: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
`;

const StyledSelectedIcon = styled.span`
  flex-shrink: 1;
  margin-left: ${tokens.spacer.x1};
  display: inline-flex;
`;

export type ClickTarget = 'selector' | 'language';

export type LanguageSelectorProps = {
  onClick?: (clickedElement: ClickTarget, locale: string) => void;
  shouldUseLocalizationDevMode?: boolean;
  kind?: 'ghost' | 'select';
  userId?: number;
  isDemoUser?: boolean;
};

type LanguageSelectorTriggerProps = {
  kind: LanguageSelectorProps['kind'];
  onClick: () => void;
  label: string;
  isBeta: boolean;
};

const LanguageSelectorTrigger = forwardRef<HTMLButtonElement, LanguageSelectorTriggerProps>(
  ({ kind, onClick, label, isBeta }: LanguageSelectorTriggerProps, ref): JSX.Element => {
    if (kind === 'ghost') {
      return (
        <Button data-testid="language-selector-dropdown" type="button" onClick={onClick} ref={ref}>
          {label}
          <Icon color="text_light" icon="caret_down_alt" size="m" />
        </Button>
      );
    }

    return (
      <DropdownSelectTrigger
        ref={ref}
        data-testid="language-selector-dropdown"
        selectedOption={{ label, value: '' }}
        onClick={onClick}
        {...(isBeta && { badge: 'beta' })}
      />
    );
  },
);

export const LanguageSelector = ({
  onClick,
  shouldUseLocalizationDevMode = false,
  kind = 'ghost',
  userId,
  isDemoUser = false,
}: LanguageSelectorProps): JSX.Element => {
  const { i18n, t } = useTranslation();
  const { showToast } = useNotify();
  const usableLocales = shouldUseLocalizationDevMode ? extendedSupportedLocales : supportedLocales;

  const currentLocale = i18n.languages?.[0];
  const selectedLocale = (usableLocales as unknown as string[]).includes(currentLocale)
    ? currentLocale
    : usableLocales[0];

  const countryPressed = async (locale: (typeof usableLocales)[number]) => {
    if (userId == null || isDemoUser) {
      i18n.changeLanguage(locale);
      onClick && onClick('language', locale);
      return;
    }
    const response = await userLocaleApi.put(userId, locale);
    if (response.success) {
      i18n.changeLanguage(locale);
      onClick && onClick('language', locale);
    } else {
      showToast({
        kind: 'danger',
        text: t('profile:language.update-error'),
      });
    }
  };

  return (
    <DropdownMenu
      trigger={
        <LanguageSelectorTrigger
          kind={kind}
          onClick={() => {
            onClick && onClick('selector', selectedLocale);
          }}
          label={localeCountryCodeMap[selectedLocale].name}
          isBeta={localeCountryCodeMap[selectedLocale].isBeta ?? false}
        />
      }
      size={kind === 'ghost' ? 's' : 'm'}
    >
      {usableLocales
        .filter((locale) => localeCountryCodeMap[locale])
        .map((locale) => (
          <DropdownMenu.BaseItem
            key={locale}
            isSelected={locale === selectedLocale}
            onClick={() => {
              if (locale !== selectedLocale) {
                countryPressed(locale);
              }
            }}
          >
            <StyledItemText>{localeCountryCodeMap[locale].name}</StyledItemText>
            {localeCountryCodeMap[locale].isBeta ?? false ? <Badge kind="beta" size="s" /> : null}
            {localeCountryCodeMap[locale].isNew ?? false ? (
              <Badge kind="custom" size="s">
                {`${t('common:badge.new')}`}
              </Badge>
            ) : null}
            {locale === selectedLocale && (
              <StyledSelectedIcon data-testid="dropdown-item-selected-icon">
                <Icon icon="selected" size="m" />
              </StyledSelectedIcon>
            )}
          </DropdownMenu.BaseItem>
        ))}
    </DropdownMenu>
  );
};
